import { useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { PS, T } from '../lib/theme';

interface Props<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  /** If provided, the row is clickable and onRowClick fires with the row's data. */
  onRowClick?: (row: T) => void;
  /** Accessible name for a directly activatable row. */
  getRowLabel?: (row: T) => string;
  /** Optional empty-state label. */
  emptyLabel?: string;
  /** Initial sort state. */
  initialSort?: SortingState;
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  getRowLabel,
  emptyLabel = 'No data',
  initialSort = [],
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const stableColumns = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data,
    columns: stableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (data.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          background: PS.white,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
          textAlign: 'center',
          color: PS.textSec,
          ...T.ui,
        }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      style={{
        background: PS.white,
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 4,
        overflowX: 'auto',
        // Setting only overflow-x makes the browser compute overflow-y to
        // `auto`, turning this into a vertical scroll container that traps
        // page scrolling on touch devices. Pin it to `hidden` so vertical
        // swipes chain to the page (nothing to clip — height fits content).
        overflowY: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
              {hg.headers.map((h) => {
                const sortable = h.column.getCanSort();
                const sort = h.column.getIsSorted();
                return (
                  <th
                    key={h.id}
                    onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      cursor: sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      ...T.label,
                    }}
                  >
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    {sort === 'asc' ? ' ▲' : sort === 'desc' ? ' ▼' : ''}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const activate = (event: MouseEvent<HTMLTableRowElement> | KeyboardEvent<HTMLTableRowElement>) => {
              if (!onRowClick || shouldKeepNestedAction(event)) return;
              onRowClick(row.original);
            };
            const label = getRowLabel?.(row.original) ?? defaultRowLabel(row.original);
            return (
            <tr
              key={row.id}
              tabIndex={onRowClick ? 0 : undefined}
              aria-label={onRowClick ? label : undefined}
              onClick={onRowClick ? activate : undefined}
              onKeyDown={onRowClick ? (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                if (shouldKeepNestedAction(event)) return;
                event.preventDefault();
                activate(event);
              } : undefined}
              style={{
                borderBottom: `1px solid ${PS.borderLight}`,
                cursor: onRowClick ? 'pointer' : 'default',
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{
                    padding: '12px 16px',
                    ...T.ui,
                    color: PS.nearBlack,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function defaultRowLabel(row: unknown): string {
  const id = typeof row === 'object' && row !== null && 'id' in row
    ? String((row as { id: unknown }).id)
    : '';
  return id ? `Open item ${id}` : 'Open item';
}

export function shouldKeepNestedAction(event: MouseEvent<HTMLTableRowElement> | KeyboardEvent<HTMLTableRowElement>): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  const nestedAction = target.closest('a, button, input, label, select, textarea, [role="button"], [role="link"], [contenteditable="true"]');
  if (nestedAction && nestedAction !== event.currentTarget) {
    return true;
  }
  if (event.type !== 'click') return false;
  const selection = window.getSelection();
  return Boolean(selection && !selection.isCollapsed && selection.toString().trim());
}
